import React from "react";
import { motion } from "framer-motion";
import { PhoneIphone, GetApp, OpenInNew, Android, Apple, ArrowForward } from "@mui/icons-material";
import { useMoreApps } from "../context/MoreAppsContext";

export const MoreAppsSection: React.FC = () => {
  const { apps, loading, openMoreApps } = useMoreApps();

  // Show first 10 apps (2 rows of 5 on desktop)
  const highlightApps = apps.slice(0, 10);

  if (loading || highlightApps.length === 0) return null;

  return (
    <section className="py-4">
      <div className="container">
        <div className="glass-panel p-4 p-md-5" style={{ borderRadius: "28px" }}>
          {/* Section Header */}
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-4">
            <div>
              <div
                className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-2.5"
                style={{
                  color: "#c084fc",
                  backgroundColor: "rgba(168, 85, 247, 0.15)",
                  border: "1px solid rgba(168, 85, 247, 0.3)",
                }}
              >
                <PhoneIphone sx={{ fontSize: 16 }} /> Ecosystem
              </div>
              <h2 className="text-3xl md:text-4xl font-extrabold text-white tracking-tight m-0">
                Our Mobile Applications
              </h2>
              <p className="text-slate-300 text-sm md:text-base mt-2 max-w-xl font-medium" style={{ color: "#cbd5e1" }}>
                Experience our suite of AI tools and games available on Android and iOS.
              </p>
            </div>
            <button
              onClick={openMoreApps}
              id="explore-all-apps-btn"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-white font-bold text-sm transition-all border-0 cursor-pointer self-start md:self-auto shadow-lg hover:scale-105"
              style={{
                background: "linear-gradient(135deg, rgba(99, 102, 241, 0.9), rgba(168, 85, 247, 0.9))",
                border: "1px solid rgba(255, 255, 255, 0.2)",
                boxShadow: "0 8px 25px rgba(99, 102, 241, 0.3)",
              }}
            >
              <span>Explore All Apps ({apps.length})</span>
              <ArrowForward sx={{ fontSize: 18 }} />
            </button>
          </div>

          {/* App Cards Grid (5 columns per row matching container bounds) */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {highlightApps.map((app, idx) => {
              const androidUrl = app.app_link?.android;
              const iosUrl = app.app_link?.ios;
              const mainUrl = androidUrl || iosUrl || "#";

              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.25, delay: idx * 0.04 }}
                  className="group p-4 rounded-2xl transition-all duration-300 flex flex-col justify-between"
                  style={{
                    background: "linear-gradient(145deg, rgba(30, 41, 59, 0.7), rgba(15, 23, 42, 0.95))",
                    border: "1px solid rgba(255, 255, 255, 0.12)",
                    boxShadow: "0 8px 25px rgba(0, 0, 0, 0.3)",
                    backdropFilter: "blur(12px)",
                  }}
                >
                  {/* Top App Content */}
                  <div className="flex flex-col items-center text-center gap-2 mb-3">
                    <img
                      src={app.app_logo}
                      alt={app.app_name}
                      className="w-14 h-14 rounded-2xl object-cover border border-white/15 shadow-md group-hover:scale-105 transition-transform"
                      style={{
                        width: "52px",
                        height: "52px",
                        borderRadius: "14px",
                        border: "1px solid rgba(255, 255, 255, 0.15)",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.4)",
                      }}
                      onError={(e) => {
                        (e.target as HTMLImageElement).src =
                          "https://snaptoon-development.s3.eu-north-1.amazonaws.com/logo/swaptoon_app_logo.png";
                      }}
                    />
                    <div className="w-full">
                      <h3
                        className="font-extrabold text-sm text-white truncate m-0 group-hover:text-indigo-300 transition-colors"
                        style={{ color: "#ffffff", fontSize: "0.875rem" }}
                        title={app.app_name}
                      >
                        {app.app_name}
                      </h3>
                      <div className="flex items-center justify-center gap-1.5 mt-1.5">
                        {androidUrl && (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md font-semibold"
                            style={{
                              backgroundColor: "rgba(16, 185, 129, 0.18)",
                              color: "#34d399",
                              border: "1px solid rgba(16, 185, 129, 0.35)",
                              fontSize: "10px",
                            }}
                          >
                            <Android sx={{ fontSize: 11 }} /> Android
                          </span>
                        )}
                        {iosUrl && (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md font-semibold"
                            style={{
                              backgroundColor: "rgba(56, 189, 248, 0.18)",
                              color: "#38bdf8",
                              border: "1px solid rgba(56, 189, 248, 0.35)",
                              fontSize: "10px",
                            }}
                          >
                            <Apple sx={{ fontSize: 11 }} /> iOS
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Bottom Full-Width CTA Button */}
                  <div className="mt-1 pt-2.5 border-t border-white/10">
                    <a
                      href={mainUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full py-2 px-3 rounded-xl text-white font-extrabold text-xs inline-flex items-center justify-center gap-1.5 transition-all text-decoration-none shadow-md group-hover:shadow-indigo-500/25"
                      style={{
                        width: "100%",
                        padding: "8px 12px",
                        borderRadius: "10px",
                        background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                        color: "#ffffff",
                        fontSize: "0.78rem",
                        fontWeight: 800,
                        boxSizing: "border-box",
                        boxShadow: "0 4px 12px rgba(99, 102, 241, 0.3)",
                      }}
                    >
                      <GetApp sx={{ fontSize: 14 }} /> Get App <OpenInNew sx={{ fontSize: 11 }} />
                    </a>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default MoreAppsSection;
